import { useRef, useMemo, useEffect, useCallback } from 'react';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import type { ExperienceAttributes } from '../../common/types.tsx';
import { useExperienceContext } from '../../common/ui/use-experience-context/index.tsx';

export type ExperienceSuccessProps = {
	experience: string;
	attributes?: ExperienceAttributes;
};

const STABLE_EMPTY_OBJECT = {};
export const useExperienceSuccess = ({
	experience,
	attributes = STABLE_EMPTY_OBJECT,
}: ExperienceSuccessProps) => {
	const experienceTracker = useExperienceContext();
	const route = useCurrentRoute();

	const self = useRef({
		experience,
		attributes,
		experienceTracker,
		route,
	});

	self.current.route = route;
	self.current.attributes = attributes;
	self.current.experienceTracker = experienceTracker;
	self.current.experience = experience;

	return useCallback(
		(extraAttributes: ExperienceAttributes = {}) => {
			const { current } = self;

			if (current.route && current.route.isRedirect === true) {
				return;
			}

			const shardAndRegionAttributes = fg('fun-1578-add-shard-and-region-to-experience-hooks')
				? { shard: getMeta('ajs-shard'), region: getMeta('ajs-region') }
				: {};

			current.experienceTracker.succeed({
				name: current.experience,
				attributes: {
					...current.attributes,
					...extraAttributes,
					...shardAndRegionAttributes,
				},
			});
		},
		[self],
	);
};

export const ExperienceSuccess = ({
	experience,
	attributes = STABLE_EMPTY_OBJECT,
}: ExperienceSuccessProps) => {
	const onExperienceSuccess = useExperienceSuccess({
		experience,
		attributes,
	});

	useEffect(() => {
		onExperienceSuccess();
	}, [attributes, onExperienceSuccess]);

	useMemo(() => {
		if (__SERVER__) {
			onExperienceSuccess();
		}
	}, [onExperienceSuccess]);

	return null;
};
